import React, { useEffect, useState, useCallback } from "react";
import { Box, Flex } from "theme-ui";
import { useNavigate } from "react-router-dom";
import {
  getActionItems, getClientActionItems, getProviderActionItems,
  getAdminOperations, getAdminPipelineSignals,
  ActionItemsData, ActionItem as ActionItemType, DashboardType,
  AdminOperationItem, PipelineSignal, AdminOperationsData, PipelineSignalsData,
} from "api/Dashboard";
import { getExpiringItems, batchDecline, batchRegenerate, lockPrice, regenerateQuote, extendRfq, reviveRfq, declineQuote, getExpirationSettings } from "api/QuoteExpiration";
import { acceptOrDeny } from "api/Quotes";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import DeclineReasonModal from "components/DeclineReasonModal";
import PriceLockModal from "components/PriceLockModal";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import { useToast } from "components/toast";
import { ActionIcon, QuestionMarkIcon, CheckMarkIcon } from "components/svg";
import styled from "styled-components";

/* ── styled header pieces ── */
const ModalIcon = styled.div`
  width: 18px; height: 18px; svg { fill: black !important; } display: flex; margin-right: 10px;
`;
const ModalTitleWrapper = styled.div` display: flex; flex-direction: column; margin-bottom: 10px; `;
const ModalTitle = styled.div` font-size: 16px; font-weight: 500; font-family: "Rubik"; color: ${themevals.colors.black}; `;
const ModalSubtitle = styled.div` font-size: 12px; color: ${themevals.colors.dkgrey}; font-family: "Poppins"; font-weight: 400; `;

/* ── types ── */
interface ExpiringItem {
  id: string; type: "quote" | "rfq"; sequence: number; projectName?: string;
  status: number; expiresAt: string; daysUntilExpiry: number;
  clientName?: string; total?: number;
}

const formatCurrency = (v: number): string => "\u0024" + v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const urgency = (d: number): "critical" | "warning" | "normal" => d <= 3 ? "critical" : d <= 7 ? "warning" : "normal";

/* ── colours ── */
const CAT: Record<string, { label: string; fg: string; bg: string }> = {
  invoice_overdue:          { label: "Overdue",    fg: "#B71C1C", bg: "#FDECEC" },
  invoice_due_soon:         { label: "Due soon",   fg: "#E65100", bg: "#FFF3E0" },
  no_project:               { label: "No project", fg: "#E65100", bg: "#FFF3E0" },
  no_po:                    { label: "No PO",      fg: "#E65100", bg: "#FFF3E0" },
  no_invoice:               { label: "No invoice", fg: "#E65100", bg: "#FFF3E0" },
  outstanding_credit:       { label: "Credit",     fg: "#1565C0", bg: "#E3F2FD" },
  provider_invoice_review:  { label: "Review",     fg: "#1565C0", bg: "#E3F2FD" },
};
const SIG: Record<string, { label: string; fg: string; bg: string }> = {
  deposit_paid:       { label: "Deposit",     fg: "#085041", bg: "#E1F5EE" },
  price_locked:       { label: "Locked",      fg: "#085041", bg: "#E1F5EE" },
  quote_regenerated:  { label: "Re-engaged",  fg: "#085041", bg: "#E1F5EE" },
  rfq_revived:        { label: "RFQ revived", fg: "#085041", bg: "#E1F5EE" },
  high_value_at_risk: { label: "At risk",     fg: "#E65100", bg: "#FFF3E0" },
};

/* ── reusable pieces ── */
const divider = "1px solid #F0F0F0";

const Badge: React.FC<{ label: string; fg: string; bg: string }> = ({ label, fg, bg }) => (
  <span style={{
    fontFamily: "Poppins, sans-serif", fontSize: "10px", fontWeight: 600, lineHeight: "14px",
    color: fg, backgroundColor: bg, padding: "1px 6px", borderRadius: "3px", whiteSpace: "nowrap", flexShrink: 0,
  }}>{label}</span>
);

const Pip: React.FC<{ color: string }> = ({ color }) => (
  <span style={{ width: 3, alignSelf: "stretch", borderRadius: "2px", backgroundColor: color, flexShrink: 0 }} />
);

/* ── Section wrapper ── */
const Section: React.FC<{ title: string; count: number; last?: boolean; right?: React.ReactNode; children: React.ReactNode }> = ({ title, count, last, right, children }) => (
  <div style={{ marginBottom: last ? 0 : 16 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: "Poppins", fontSize: 12, fontWeight: 600, color: themevals.colors.black }}>{title}</span>
        <span style={{ fontFamily: "Poppins", fontSize: 10, fontWeight: 600, color: "#888", background: "#F2F2F2", padding: "0 6px", borderRadius: 8, lineHeight: "18px" }}>{count}</span>
      </div>
      {right}
    </div>
    <div style={{ border: divider, borderRadius: 6, overflow: "hidden" }}>
      {children}
    </div>
  </div>
);

/* ── Operation row ── */
const OpRow: React.FC<{ item: AdminOperationItem; onAction: () => void; isLast: boolean; selected?: boolean; onToggle?: () => void }> = ({ item, onAction, isLast, selected, onToggle }) => {
  const c = CAT[item.category] || { label: item.category, fg: "#888", bg: "#F0F0F0" };
  const pipColor = item.priority === "critical" ? "#B71C1C" : item.priority === "warning" ? "#E65100" : "transparent";
  const isCrit = item.priority === "critical";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderBottom: isLast ? "none" : divider, cursor: onToggle ? "pointer" : "default", backgroundColor: selected ? "#F8F9FA" : "transparent" }} onClick={onToggle}>
      {onToggle && <input type="checkbox" checked={!!selected} onChange={onToggle} onClick={(e) => e.stopPropagation()} style={{ width: 14, height: 14, cursor: "pointer", flexShrink: 0, margin: 0 }} />}
      <Pip color={pipColor} />
      <Badge label={c.label} fg={c.fg} bg={c.bg} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins", fontSize: 12, fontWeight: 500, color: themevals.colors.black, lineHeight: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.title}
        </div>
        <div style={{ fontFamily: "Poppins", fontSize: 11, color: "#888", lineHeight: "16px" }}>
          {item.subtitle}
        </div>
      </div>
      <button onClick={onAction} style={{
        fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0,
        border: isCrit ? "none" : "1px solid #D0D0D0", borderRadius: 4,
        padding: "3px 10px", lineHeight: "18px",
        color: isCrit ? "#fff" : themevals.colors.black,
        backgroundColor: isCrit ? "#B71C1C" : "transparent",
      }}>{item.actionLabel}</button>
    </div>
  );
};

/* ── Signal row ── */
const SigRow: React.FC<{ item: PipelineSignal; onView: () => void; isLast: boolean }> = ({ item, onView, isLast }) => {
  const s = SIG[item.signalType] || { label: item.signalType, fg: "#085041", bg: "#E1F5EE" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderBottom: isLast ? "none" : divider }}>
      <Badge label={s.label} fg={s.fg} bg={s.bg} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins", fontSize: 12, fontWeight: 500, color: themevals.colors.black, lineHeight: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.title}
        </div>
        <div style={{ fontFamily: "Poppins", fontSize: 11, color: "#888", lineHeight: "16px" }}>
          {item.subtitle}
        </div>
      </div>
      <button onClick={onView} style={{
        fontFamily: "Poppins", fontSize: 11, fontWeight: 500, cursor: "pointer", flexShrink: 0,
        border: "1px solid #D0D0D0", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
        color: themevals.colors.black, backgroundColor: "transparent",
      }}>View</button>
    </div>
  );
};

/* ── Expiring row ── */
const ExpRow: React.FC<{ item: ExpiringItem; selected: boolean; onToggle: () => void; onView: () => void; onLockPrice?: () => void; onRegenerate?: () => void; onExtend?: () => void; onReopen?: () => void; actionLoading?: boolean; isLast: boolean }> = ({ item, selected, onToggle, onView, onLockPrice, onRegenerate, onExtend, onReopen, actionLoading, isLast }) => {
  const u = urgency(item.daysUntilExpiry);
  const prefix = item.type === "quote" ? "GMQ" : "GMR";
  const seq = prefix + "-" + String(item.sequence).padStart(5, "0");
  const daysText = item.daysUntilExpiry <= 0 ? "Expired" : item.daysUntilExpiry + "d left";
  const daysColor = u === "critical" ? "#B71C1C" : u === "warning" ? "#E65100" : "#888";
  const pipColor = u === "critical" ? "#B71C1C" : u === "warning" ? "#E65100" : "transparent";
  const isExpired = item.daysUntilExpiry <= 0 || (item.type === "quote" ? item.status === 4 : item.status === 6);
  const isQuote = item.type === "quote";

  return (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderBottom: isLast ? "none" : divider, cursor: "pointer", backgroundColor: selected ? "#F8F9FA" : "transparent" }}>
      <Pip color={pipColor} />
      <input type="checkbox" checked={selected} onChange={onToggle} onClick={(e) => e.stopPropagation()} style={{ width: 14, height: 14, cursor: "pointer", flexShrink: 0, margin: 0 }} />
      <Badge label={isQuote ? "Quote" : "RFQ"} fg={isQuote ? themevals.colors.info : themevals.colors.primary} bg={isQuote ? themevals.colors.info + "15" : themevals.colors.primary + "15"} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins", fontSize: 12, fontWeight: 500, color: themevals.colors.black, lineHeight: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {seq}{item.projectName ? " \u2014 " + item.projectName : ""}
        </div>
        <div style={{ fontFamily: "Poppins", fontSize: 11, lineHeight: "16px", color: "#888" }}>
          {item.clientName || ""}{item.clientName ? " \u00B7 " : ""}<span style={{ color: daysColor, fontWeight: 600 }}>{daysText}</span>
        </div>
      </div>
      {item.total != null && item.total > 0 && (
        <span style={{ fontFamily: "Poppins", fontSize: 11, fontWeight: 600, color: "#666", flexShrink: 0 }}>{formatCurrency(item.total)}</span>
      )}
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        {isQuote && !isExpired && onLockPrice && (
          <button onClick={(e: any) => { e.stopPropagation(); onLockPrice(); }} disabled={actionLoading} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: actionLoading ? "wait" : "pointer",
            border: "none", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#fff", backgroundColor: themevals.colors.info, opacity: actionLoading ? 0.6 : 1,
          }}>Lock Price</button>
        )}
        {isQuote && isExpired && onRegenerate && (
          <button onClick={(e: any) => { e.stopPropagation(); onRegenerate(); }} disabled={actionLoading} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: actionLoading ? "wait" : "pointer",
            border: "none", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#fff", backgroundColor: "#E65100", opacity: actionLoading ? 0.6 : 1,
          }}>Regenerate</button>
        )}
        {!isQuote && !isExpired && onExtend && (
          <button onClick={(e: any) => { e.stopPropagation(); onExtend(); }} disabled={actionLoading} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: actionLoading ? "wait" : "pointer",
            border: "none", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#fff", backgroundColor: "#C05621", opacity: actionLoading ? 0.6 : 1,
          }}>Extend</button>
        )}
        {!isQuote && isExpired && onReopen && (
          <button onClick={(e: any) => { e.stopPropagation(); onReopen(); }} disabled={actionLoading} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: actionLoading ? "wait" : "pointer",
            border: "none", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#fff", backgroundColor: "#E65100", opacity: actionLoading ? 0.6 : 1,
          }}>Reopen</button>
        )}
        <button onClick={(e: any) => { e.stopPropagation(); onView(); }} style={{
          fontFamily: "Poppins", fontSize: 11, fontWeight: 500, cursor: "pointer",
          border: "1px solid #D0D0D0", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
          color: themevals.colors.black, backgroundColor: "transparent",
        }}>View</button>
      </div>
    </div>
  );
};

/* ── Client action item row ── */
const ClientRow: React.FC<{ item: ActionItemType; onReview: () => void; onLockPrice?: () => void; onDecline?: () => void; onAccept?: () => void; selected?: boolean; onToggle?: () => void; isLast: boolean }> = ({ item, onReview, onLockPrice, onDecline, onAccept, selected, onToggle, isLast }) => {
  const pipColor = item.priority === "critical" ? "#B71C1C" : item.priority === "warning" ? "#E65100" : "transparent";

  // Calculate expiration countdown from dueDate if available
  let expiryText: string | null = null;
  let expiryColor = "#888";
  if (item.dueDate) {
    const daysLeft = Math.ceil((new Date(item.dueDate).getTime() - Date.now()) / 86400000);
    if (daysLeft <= 0) { expiryText = "Expired"; expiryColor = "#B71C1C"; }
    else if (daysLeft <= 3) { expiryText = daysLeft + "d left"; expiryColor = "#B71C1C"; }
    else if (daysLeft <= 7) { expiryText = daysLeft + "d left"; expiryColor = "#E65100"; }
    else { expiryText = daysLeft + "d left"; expiryColor = "#888"; }
  }

  const isQuote = item.entityType === "quote";
  const isInvoice = item.entityType === "invoice";
  const showLockPrice = isQuote && onLockPrice && item.type === "quote_pending";

  // Build subtitle: use expiry countdown if available, otherwise fall back to original subtitle
  const subtitleParts: React.ReactNode[] = [];
  // Strip "Pending X days" from subtitle if we have expiry info
  const baseSubtitle = expiryText && item.subtitle ? item.subtitle.replace(/Pending \d+ days?/, "").replace(/^\s*[·]\s*/, "").trim() : item.subtitle;
  if (baseSubtitle) subtitleParts.push(baseSubtitle);
  if (expiryText && subtitleParts.length > 0) subtitleParts.push(" \u00B7 ");
  if (expiryText) subtitleParts.push(React.createElement("span", { style: { color: expiryColor, fontWeight: 600 } }, expiryText));

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderBottom: isLast ? "none" : divider, cursor: onToggle ? "pointer" : "default", backgroundColor: selected ? "#F8F9FA" : "transparent" }} onClick={onToggle ? onToggle : undefined}>
      <Pip color={pipColor} />
      {onToggle && <input type="checkbox" checked={!!selected} onChange={onToggle} onClick={(e) => e.stopPropagation()} style={{ width: 14, height: 14, cursor: "pointer", flexShrink: 0, margin: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins", fontSize: 12, fontWeight: 500, color: themevals.colors.black, lineHeight: "18px" }}>{item.title}</div>
        <div style={{ fontFamily: "Poppins", fontSize: 11, color: "#888", lineHeight: "16px" }}>{subtitleParts}</div>
      </div>
      {item.amount != null && item.amount > 0 && (
        <span style={{ fontFamily: "Poppins", fontSize: 11, fontWeight: 600, color: "#666", flexShrink: 0 }}>{formatCurrency(item.amount)}</span>
      )}
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        {isQuote && onAccept && (
          <button onClick={(e: any) => { e.stopPropagation(); onAccept(); }} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: "pointer",
            border: "none", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#fff", backgroundColor: "var(--theme-ui-colors-success)",
          }}>Accept</button>
        )}
        {showLockPrice && (
          <button onClick={(e: any) => { e.stopPropagation(); if (onLockPrice) onLockPrice(); }} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: "pointer",
            border: "none", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#fff", backgroundColor: themevals.colors.info,
          }}>Lock Price</button>
        )}
        {isQuote && onDecline && (
          <button onClick={(e: any) => { e.stopPropagation(); onDecline(); }} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: "pointer",
            border: "1px solid #B71C1C", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#B71C1C", backgroundColor: "transparent",
          }}>Decline</button>
        )}
        {isInvoice && item.priority === "critical" ? (
          <button onClick={onReview} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0,
            border: "none", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: "#fff", backgroundColor: "#B71C1C",
          }}>Pay Now</button>
        ) : (
          <button onClick={onReview} style={{
            fontFamily: "Poppins", fontSize: 11, fontWeight: 500, cursor: "pointer", flexShrink: 0,
            border: "1px solid #D0D0D0", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
            color: themevals.colors.black, backgroundColor: "transparent",
          }}>View</button>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════
   MODAL
   ════════════════════════════════════════════════ */

interface ActionItemsModalProps { onClose: () => void; onChange?: () => void; dashboardType?: DashboardType; }

const ActionItemsModal: React.FC<ActionItemsModalProps> = ({ onClose, onChange, dashboardType = "admin" }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = dashboardType === "admin" || dashboardType === "manager";

  /* state */
  const [operations, setOperations] = useState<AdminOperationsData | null>(null);
  const [signals, setSignals] = useState<PipelineSignalsData | null>(null);
  const [adminLoading, setAdminLoading] = useState(true);
  const [actionData, setActionData] = useState<ActionItemsData | null>(null);
  const [actionLoading, setActionLoading] = useState(true);
  const [expiringItems, setExpiringItems] = useState<ExpiringItem[]>([]);
  const [expiringLoading, setExpiringLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchDecline, setShowBatchDecline] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [quickActionId, setQuickActionId] = useState<string | null>(null);
  const [lockTarget, setLockTarget] = useState<ExpiringItem | null>(null);
  const [lockSettings, setLockSettings] = useState<any>(null);
  const [lockSubmitting, setLockSubmitting] = useState(false);
  const [regenTarget, setRegenTarget] = useState<ExpiringItem | null>(null);
  const [regenConfirmSubmitting, setRegenConfirmSubmitting] = useState(false);
  const [actSelectedIds, setActSelectedIds] = useState<Set<string>>(new Set());
  const [showActBatchDecline, setShowActBatchDecline] = useState(false);
  const [actBatchSubmitting, setActBatchSubmitting] = useState(false);
  const [declineTarget, setDeclineTarget] = useState<ActionItemType | null>(null);
  const [opsMaxAge, setOpsMaxAge] = useState(90);
  const [opsSelectedIds, setOpsSelectedIds] = useState<Set<string>>(new Set());
  const [dismissedOps, setDismissedOps] = useState<Set<string>>(() => {
    try { const saved = localStorage.getItem('dismissed_ops'); return saved ? new Set(JSON.parse(saved)) : new Set(); } catch { return new Set(); }
  });
  const [acceptTarget, setAcceptTarget] = useState<ActionItemType | null>(null);
  const [acceptSubmitting, setAcceptSubmitting] = useState(false);

  /* ops dismiss */
  const opsToggle = (id: string) => { setOpsSelectedIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const handleDismissOps = () => {
    const merged = new Set([...dismissedOps, ...opsSelectedIds]);
    setDismissedOps(merged);
    try { localStorage.setItem('dismissed_ops', JSON.stringify([...merged])); } catch {}
    setOpsSelectedIds(new Set());
    toast.add('Dismissed ' + opsSelectedIds.size + ' item' + (opsSelectedIds.size === 1 ? '' : 's'), 'var(--theme-ui-colors-success)');
    onChange?.();
  };

  /* inline accept */
  const handleAcceptQuote = async () => {
    if (!acceptTarget) return;
    setAcceptSubmitting(true);
    try {
      const res = await acceptOrDeny({ data: { id: acceptTarget.entityId }, update: { status: 1, discountFlag: false } });
      if (res?.quote) {
        toast.add(res.quote.project ? 'Quote accepted. Project created.' : 'Quote accepted.', 'var(--theme-ui-colors-success)');
        setAcceptTarget(null);
        if (res.quote.project?.id) {
          onClose();
          navigate('/projects/' + res.quote.project.id);
        } else {
          const r = dashboardType === 'client' ? await getClientActionItems(100) : await getActionItems(100);
          setActionData(r);
          fetchExpiring();
        }
      } else { toast.add(res?.message || 'Failed to accept.', 'var(--theme-ui-colors-danger)'); }
    } catch { toast.add('Error accepting quote.', 'var(--theme-ui-colors-danger)'); }
    setAcceptSubmitting(false);
  };

  /* admin fetch */
  useEffect(() => {
    if (!isAdmin) { setAdminLoading(false); return; }
    (async () => {
      setAdminLoading(true);
      const [o, s] = await Promise.all([getAdminOperations(opsMaxAge), getAdminPipelineSignals()]);
      setOperations(o); setSignals(s);
      if (!o && !s) { const fb = await getActionItems(100); setActionData(fb); }
      setAdminLoading(false);
    })();
  }, [isAdmin, opsMaxAge]);

  /* client fetch */
  useEffect(() => {
    if (isAdmin) { setActionLoading(false); return; }
    (async () => {
      const r = dashboardType === "client" ? await getClientActionItems(100) : dashboardType === "provider" ? await getProviderActionItems(100) : await getActionItems(100);
      setActionData(r); setActionLoading(false);
    })();
  }, [isAdmin, dashboardType]);

  /* expiring fetch */
  const fetchExpiring = useCallback(async () => {
    setExpiringLoading(true);
    const data = await getExpiringItems();
    const now = new Date();
    const combined: ExpiringItem[] = [];
    if (Array.isArray(data?.quotes)) for (const q of data.quotes) combined.push({ id: q.id, type: "quote", sequence: q.sequence, projectName: q.projectName || q.quoteRequest?.projectName, status: q.status, expiresAt: q.expiresAt, daysUntilExpiry: Math.ceil((new Date(q.expiresAt).getTime() - now.getTime()) / 86400000), clientName: q.client?.name || q.primaryClient?.name, total: q.total });
    if (Array.isArray(data?.rfqs)) for (const r of data.rfqs) combined.push({ id: r.id, type: "rfq", sequence: r.sequence, projectName: r.projectName, status: r.status, expiresAt: r.expiresAt, daysUntilExpiry: Math.ceil((new Date(r.expiresAt).getTime() - now.getTime()) / 86400000), clientName: r.client?.name || r.primaryClient?.name });
    combined.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
    setExpiringItems(combined); setSelectedIds(new Set()); setExpiringLoading(false);
  }, []);
  useEffect(() => { if (dashboardType !== "provider") fetchExpiring(); else setExpiringLoading(false); }, [dashboardType, fetchExpiring]);

  /* nav handlers */
  const go = (path: string) => { onClose(); navigate(path); };
  const handleOp = (i: AdminOperationItem) => go(i.entityType === "invoice" ? "/invoices/" + i.entityId : i.entityType === "quote" ? "/quotes/" + i.entityId : i.entityType === "project" ? "/projects/" + i.entityId : "/provider-invoices/" + i.entityId);
  const handleSig = (i: PipelineSignal) => go(i.entityType === "quote" ? "/quotes/" + i.entityId : "/quote-requests/" + i.entityId);
  const handleExp = (i: ExpiringItem) => go(i.type === "quote" ? "/quotes/" + i.id : "/quote-requests/" + i.id);
  const handleReview = (i: ActionItemType) => { if (i.entityType === "invoice") go("/invoices/" + i.entityId); else if (i.entityType === "quote") go("/quotes/" + i.entityId); else if (i.entityType === "project") go("/projects/" + i.entityId); };

  /* selection */
  const toggle = (id: string, t: string) => { const k = t + ":" + id; setSelectedIds(p => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; }); };
  const toggleAll = () => { selectedIds.size === expiringItems.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(expiringItems.map(i => i.type + ":" + i.id))); };
  const split = () => { const q: string[] = [], r: string[] = []; for (const k of selectedIds) { const [t, id] = k.split(":"); t === "quote" ? q.push(id) : r.push(id); } return { quoteIds: q, rfqIds: r }; };
  const selQuotes = Array.from(selectedIds).filter(k => k.startsWith("quote:")).length;

  /* batch actions */
  const handleDecline = async (d: { category: string | null; notes?: string; competitorName?: string }) => {
    setBatchSubmitting(true);
    try { const r = await batchDecline({ ...split(), ...d }); if (r.success !== false) { toast.add("Declined " + selectedIds.size + " item" + (selectedIds.size === 1 ? "" : "s"), "var(--theme-ui-colors-success)"); setShowBatchDecline(false); setSelectedIds(new Set()); fetchExpiring(); } else toast.add(r.message || "Failed", "var(--theme-ui-colors-danger)"); } catch { toast.add("Batch decline failed", "var(--theme-ui-colors-danger)"); }
    setBatchSubmitting(false);
  };
  const handleRegen = async () => {
    const { quoteIds } = split(); if (!quoteIds.length) return; setRegenSubmitting(true);
    try { const r = await batchRegenerate(quoteIds); if (r.success !== false) { toast.add("Regenerated " + (r.regenerated || quoteIds.length) + " quote(s)", "var(--theme-ui-colors-success)"); setSelectedIds(new Set()); fetchExpiring(); } else toast.add(r.message || "Failed", "var(--theme-ui-colors-danger)"); } catch { toast.add("Regeneration failed", "var(--theme-ui-colors-danger)"); }
    setRegenSubmitting(false);
  };

  const handleQuickLock = async (item: ExpiringItem) => {
    // Fetch settings for the PriceLockModal, then show it
    if (!lockSettings) {
      try {
        const s = await getExpirationSettings();
        setLockSettings(s);
      } catch (e: any) { /* use defaults */ }
    }
    setLockTarget(item);
  };

  const confirmLockPrice = async () => {
    if (!lockTarget) return;
    setLockSubmitting(true);
    try {
      const res = await lockPrice(lockTarget.id);
      if (res?.success !== false && !res?.message?.includes('Cannot')) {
        toast.add('Price locked successfully.', 'var(--theme-ui-colors-success)');
        setLockTarget(null);
        fetchExpiring();
      } else { toast.add(res?.message || 'Failed to lock price.', 'var(--theme-ui-colors-danger)'); }
    } catch (e: any) { toast.add('Error locking price.', 'var(--theme-ui-colors-danger)'); }
    setLockSubmitting(false);
  };

  const handleQuickRegen = (item: ExpiringItem) => {
    setRegenTarget(item);
  };

  const handleQuickExtend = async (item: ExpiringItem) => {
    try {
      const res = await extendRfq(item.id);
      if (res?.success) {
        toast.add('RFQ extended to ' + (res.newExpiresAt ? new Date(res.newExpiresAt).toLocaleDateString() : 'new date'), 'var(--theme-ui-colors-success)');
        fetchExpiring();
      } else { toast.add(res?.message || 'Failed to extend.', 'var(--theme-ui-colors-danger)'); }
    } catch (e: any) { toast.add('Error extending RFQ.', 'var(--theme-ui-colors-danger)'); }
  };

  const handleQuickReopen = async (item: ExpiringItem) => {
    setQuickActionId(item.id);
    try {
      const res = await reviveRfq(item.id);
      if (res?.success) {
        toast.add('RFQ reopened with updated pricing.', 'var(--theme-ui-colors-success)');
        fetchExpiring();
      } else { toast.add(res?.message || 'Failed to reopen.', 'var(--theme-ui-colors-danger)'); }
    } catch (e: any) { toast.add('Error reopening RFQ.', 'var(--theme-ui-colors-danger)'); }
    setQuickActionId(null);
  };

  const confirmRegenerate = async () => {
    if (!regenTarget) return;
    setRegenConfirmSubmitting(true);
    try {
      const res = await regenerateQuote(regenTarget.id);
      if (res?.newQuoteId) {
        toast.add('Quote regenerated at current pricing.', 'var(--theme-ui-colors-success)');
        setRegenTarget(null);
        fetchExpiring();
      } else { toast.add(res?.message || 'Failed to regenerate.', 'var(--theme-ui-colors-danger)'); }
    } catch (e: any) { toast.add('Error regenerating quote.', 'var(--theme-ui-colors-danger)'); }
    setRegenConfirmSubmitting(false);
  };

  /* derived */
  const loading = isAdmin ? (adminLoading || expiringLoading) : (actionLoading || expiringLoading);
  const ops = (operations?.items || []).filter(o => !dismissedOps.has(o.id));
  const sigs = signals?.items || [];
  const adminNew = operations !== null || signals !== null;
  const exp = expiringItems;
  const acts = (actionData?.items || []).filter(a => !new Set(exp.map(e => e.id)).has(a.entityId));
  const hasOps = ops.length > 0, hasSigs = sigs.length > 0, hasExp = exp.length > 0, hasActs = acts.length > 0;
  const actToggle = (id: string) => { setActSelectedIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const actToggleAll = () => { actSelectedIds.size === acts.filter(a => a.entityType === 'quote').length ? setActSelectedIds(new Set()) : setActSelectedIds(new Set(acts.filter(a => a.entityType === 'quote').map(a => a.entityId))); };
  const actQuoteCount = acts.filter(a => a.entityType === 'quote').length;
  const adminEmpty = isAdmin && adminNew && !hasOps && !hasExp && !hasSigs;
  const clientEmpty = !isAdmin && !hasExp && !hasActs;
  const handleActDecline = async (d: { category: string | null; notes?: string; competitorName?: string }) => {
    if (declineTarget) {
      // Single item decline
      setActBatchSubmitting(true);
      try {
        const res = await declineQuote(declineTarget.entityId, d);
        if (res?.success) {
          toast.add('Quote declined.', 'var(--theme-ui-colors-success)');
          setDeclineTarget(null);
          // Refresh client action items
          const r = dashboardType === 'client' ? await getClientActionItems(100) : await getActionItems(100);
          setActionData(r);
          fetchExpiring();
        } else { toast.add(res?.message || 'Failed to decline.', 'var(--theme-ui-colors-danger)'); }
      } catch { toast.add('Decline failed.', 'var(--theme-ui-colors-danger)'); }
      setActBatchSubmitting(false);
    } else if (showActBatchDecline && actSelectedIds.size > 0) {
      // Batch decline from Other Action Items
      setActBatchSubmitting(true);
      try {
        const quoteIds = Array.from(actSelectedIds);
        const r = await batchDecline({ quoteIds, rfqIds: [], ...d });
        if (r.success !== false) {
          toast.add('Declined ' + quoteIds.length + ' item' + (quoteIds.length === 1 ? '' : 's'), 'var(--theme-ui-colors-success)');
          setShowActBatchDecline(false);
          setActSelectedIds(new Set());
          const refreshed = dashboardType === 'client' ? await getClientActionItems(100) : await getActionItems(100);
          setActionData(refreshed);
          fetchExpiring();
        } else { toast.add(r.message || 'Failed', 'var(--theme-ui-colors-danger)'); }
      } catch { toast.add('Batch decline failed', 'var(--theme-ui-colors-danger)'); }
      setActBatchSubmitting(false);
    }
  };

  const batchType: "quote" | "rfq" = Array.from(selectedIds).some(k => k.startsWith("quote:")) ? "quote" : "rfq";

  /* batch action buttons for expiring section */
  const expRight = (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      {selQuotes > 0 && <BasicButton scheme="success" onClick={handleRegen} $submitting={regenSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText styles={{ fontSize: "10px", padding: "2px 8px" }}>Regenerate ({selQuotes})</BasicButton>}
      {selectedIds.size > 0 && <BasicButton scheme="danger" outline onClick={() => setShowBatchDecline(true)} styles={{ fontSize: "10px", padding: "2px 8px" }}>Decline ({selectedIds.size})</BasicButton>}
    </div>
  );

  return (
    <>
      {!lockTarget && !acceptTarget && <Modal isOpen={true} toggle={onClose} centered size="lg">
        <ModalHeader toggle={onClose}>
          <ModalIcon><ActionIcon fill={"000"} width="18px" /></ModalIcon>
          <ModalTitleWrapper><ModalTitle>Action Items</ModalTitle><ModalSubtitle>Operations and pipeline signals for your review.</ModalSubtitle></ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          <div style={{ margin: "-20px", overflow: "hidden" }}>
          <div style={{ maxHeight: "62vh", overflowY: "auto", padding: "16px 20px" }}>
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 180, color: "#999", fontFamily: "Poppins", fontSize: 13 }}>Loading...</div>
            ) : (adminEmpty || clientEmpty) ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 180, color: "#999", fontFamily: "Poppins", fontSize: 13 }}>Nothing needs your attention right now.</div>
            ) : isAdmin && adminNew ? (
              <>
                {hasOps && (
                  <Section title="Operations" count={ops.length} last={!hasExp && !hasSigs} right={
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {opsSelectedIds.size > 0 && <button onClick={handleDismissOps} style={{ fontFamily: 'Poppins', fontSize: 10, fontWeight: 600, padding: '2px 8px', border: '1px solid #D0D0D0', borderRadius: 4, cursor: 'pointer', color: '#666', backgroundColor: '#fff' }}>Dismiss ({opsSelectedIds.size})</button>}
                      <select value={opsMaxAge} onChange={(e) => setOpsMaxAge(Number(e.target.value))} style={{ fontFamily: 'Poppins', fontSize: 10, padding: '2px 6px', border: '1px solid #D0D0D0', borderRadius: 4, color: '#666', backgroundColor: '#fff', cursor: 'pointer' }}>
                        <option value={30}>Last 30 days</option>
                        <option value={60}>Last 60 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={180}>Last 180 days</option>
                        <option value={3650}>All time</option>
                      </select>
                    </div>
                  }>
                    {ops.length > 1 && (
                      <div onClick={() => { opsSelectedIds.size === ops.length ? setOpsSelectedIds(new Set()) : setOpsSelectedIds(new Set(ops.map(o => o.id))); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderBottom: divider, cursor: 'pointer', backgroundColor: '#FAFAFA' }}>
                        <input type="checkbox" checked={opsSelectedIds.size === ops.length && ops.length > 0} onChange={() => { /* handled by parent onClick */ }} style={{ width: 14, height: 14, cursor: 'pointer', margin: 0 }} />
                        <span style={{ fontFamily: 'Poppins', fontSize: 11, color: '#888' }}>{opsSelectedIds.size > 0 ? opsSelectedIds.size + ' selected' : 'Select all'}</span>
                      </div>
                    )}
                    {ops.map((item, i) => <OpRow key={item.id} item={item} onAction={() => handleOp(item)} isLast={i === ops.length - 1} selected={opsSelectedIds.has(item.id)} onToggle={() => opsToggle(item.id)} />)}
                  </Section>
                )}
                {hasExp && (
                  <Section title="Expiring Quotes & RFQs" count={exp.length} last={!hasSigs} right={expRight}>
                    {exp.length > 1 && (
                      <div onClick={toggleAll} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 12px", borderBottom: divider, cursor: "pointer", backgroundColor: "#FAFAFA" }}>
                        <input type="checkbox" checked={selectedIds.size === exp.length} onChange={toggleAll} style={{ width: 14, height: 14, cursor: "pointer", margin: 0 }} />
                        <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#888" }}>{selectedIds.size > 0 ? selectedIds.size + " selected" : "Select all"}</span>
                      </div>
                    )}
                    {exp.map((item, i) => <ExpRow key={item.type + item.id} item={item} selected={selectedIds.has(item.type + ":" + item.id)} onToggle={() => toggle(item.id, item.type)} onView={() => handleExp(item)} onLockPrice={item.type === "quote" ? () => handleQuickLock(item) : undefined} onRegenerate={item.type === "quote" ? () => handleQuickRegen(item) : undefined} onExtend={item.type === "rfq" ? () => handleQuickExtend(item) : undefined} onReopen={item.type === "rfq" && item.daysUntilExpiry <= 0 ? () => handleQuickReopen(item) : undefined} actionLoading={quickActionId === item.id} isLast={i === exp.length - 1} />)}
                  </Section>
                )}
                {hasSigs && (
                  <Section title="Pipeline Signals" count={sigs.length} last>
                    {sigs.map((item, i) => <SigRow key={item.id} item={item} onView={() => handleSig(item)} isLast={i === sigs.length - 1} />)}
                  </Section>
                )}
              </>
            ) : (
              <>
                {hasExp && (
                  <Section title="Expiring Quotes & RFQs" count={exp.length} last={!hasActs} right={expRight}>
                    {exp.length > 1 && (
                      <div onClick={toggleAll} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 12px", borderBottom: divider, cursor: "pointer", backgroundColor: "#FAFAFA" }}>
                        <input type="checkbox" checked={selectedIds.size === exp.length} onChange={toggleAll} style={{ width: 14, height: 14, cursor: "pointer", margin: 0 }} />
                        <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#888" }}>{selectedIds.size > 0 ? selectedIds.size + " selected" : "Select all"}</span>
                      </div>
                    )}
                    {exp.map((item, i) => <ExpRow key={item.type + item.id} item={item} selected={selectedIds.has(item.type + ":" + item.id)} onToggle={() => toggle(item.id, item.type)} onView={() => handleExp(item)} onLockPrice={item.type === "quote" ? () => handleQuickLock(item) : undefined} onRegenerate={item.type === "quote" ? () => handleQuickRegen(item) : undefined} onExtend={item.type === "rfq" ? () => handleQuickExtend(item) : undefined} onReopen={item.type === "rfq" && item.daysUntilExpiry <= 0 ? () => handleQuickReopen(item) : undefined} actionLoading={quickActionId === item.id} isLast={i === exp.length - 1} />)}
                  </Section>
                )}
                {hasActs && (
                  <Section title="Other Action Items" count={acts.length} last right={
                    actSelectedIds.size > 0 ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <BasicButton scheme="danger" outline onClick={() => setShowActBatchDecline(true)} styles={{ fontSize: "10px", padding: "2px 8px" }}>Decline ({actSelectedIds.size})</BasicButton>
                      </div>
                    ) : undefined
                  }>
                    {actQuoteCount > 1 && (
                      <div onClick={actToggleAll} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 12px", borderBottom: divider, cursor: "pointer", backgroundColor: "#FAFAFA" }}>
                        <input type="checkbox" checked={actSelectedIds.size === actQuoteCount} onChange={actToggleAll} style={{ width: 14, height: 14, cursor: "pointer", margin: 0 }} />
                        <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#888" }}>{actSelectedIds.size > 0 ? actSelectedIds.size + " selected" : "Select all quotes"}</span>
                      </div>
                    )}
                    {acts.map((item, i) => <ClientRow key={item.id} item={item} onReview={() => handleReview(item)} onLockPrice={item.entityType === "quote" && item.type === "quote_pending" ? () => handleQuickLock({ id: item.entityId, type: "quote", sequence: parseInt(item.entityNumber?.replace(/\D/g, "") || "0", 10), total: item.amount || 0, expiresAt: item.dueDate || "", daysUntilExpiry: item.dueDate ? Math.ceil((new Date(item.dueDate).getTime() - Date.now()) / 86400000) : 30, status: 0 }) : undefined} onAccept={item.entityType === "quote" && item.type === "quote_pending" ? () => setAcceptTarget(item) : undefined} onDecline={item.entityType === "quote" ? () => setDeclineTarget(item) : undefined} selected={actSelectedIds.has(item.entityId)} onToggle={item.entityType === "quote" ? () => actToggle(item.entityId) : undefined} isLast={i === acts.length - 1} />)}
                  </Section>
                )}
              </>
            )}
          </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <BasicButton type="button" outline onClick={onClose} scheme="secondary">Close</BasicButton>
        </ModalFooter>
      </Modal>}
      {showBatchDecline && <DeclineReasonModal type={batchType} title={"Decline " + selectedIds.size + " Item" + (selectedIds.size === 1 ? "" : "s")} onConfirm={handleDecline} onCancel={() => setShowBatchDecline(false)} submitting={batchSubmitting} />}

      {(showActBatchDecline || declineTarget) && <DeclineReasonModal type="quote" title={declineTarget ? "Decline Quote" : "Decline " + actSelectedIds.size + " Quote" + (actSelectedIds.size === 1 ? "" : "s")} onConfirm={handleActDecline} onCancel={() => { setShowActBatchDecline(false); setDeclineTarget(null); }} submitting={actBatchSubmitting} />}

      {acceptTarget && (
        <BasicModal
          title="Accept Quote?"
          cancel_click={() => setAcceptTarget(null)}
          cancel_text="No, cancel"
          $submitting={acceptSubmitting}
          size="sm"
          icon={<QuestionMarkIcon fill="#818181" height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={handleAcceptQuote}
              $submitting={acceptSubmitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!acceptSubmitting && <CheckMarkIcon fill="#fff" />}
              {"Accept quote"}
            </BasicButton>
          }
        >
          <P lineHeight="22px" marginBottom="0px">
            {"Are you sure you want to accept "}
            <strong>{acceptTarget.entityNumber || acceptTarget.title}</strong>
            {acceptTarget.amount ? " (" + formatCurrency(acceptTarget.amount) + ")" : ""}
            {"?"}
          </P>
        </BasicModal>
      )}

      {lockTarget && (
        <PriceLockModal
          quote={{ id: lockTarget.id, total: lockTarget.total || 0, sequence: lockTarget.sequence, expiresAt: lockTarget.expiresAt, majorVersion: 1, minorVersion: 0 }}
          settings={lockSettings || undefined}
          onComplete={() => { setLockTarget(null); fetchExpiring(); onClose(); }}
          onCancel={() => setLockTarget(null)}
        />
      )}

      {regenTarget && (
        <Modal isOpen={true} toggle={() => setRegenTarget(null)} size="md" centered={true}>
          <ModalHeader toggle={() => setRegenTarget(null)}>
            <div style={{ fontFamily: "Rubik", fontSize: 16, fontWeight: 500 }}>Regenerate Quote?</div>
          </ModalHeader>
          <ModalBody>
            <div style={{ fontFamily: "Poppins", fontSize: 13, color: themevals.colors.dkgrey, lineHeight: 1.6 }}>
              This will create a new version of <strong>{'GMQ-' + String(regenTarget.sequence).padStart(5, '0')}</strong> repriced at current rates.
              {regenTarget.projectName ? ' (' + regenTarget.projectName + ')' : ''}
            </div>
          </ModalBody>
          <ModalFooter>
            <BasicButton scheme="secondary" outline onClick={() => setRegenTarget(null)} styles={{ margin: '0 10px .25rem 0' }}>Cancel</BasicButton>
            <BasicButton scheme="primary" onClick={confirmRegenerate} $submitting={regenConfirmSubmitting} $loadingColor="#fff" keepText>Regenerate</BasicButton>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default ActionItemsModal;
